import { Box, Button, Checkbox, Chip, CircularProgress, FormControl, InputLabel, Link, ListItemText, makeStyles, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@material-ui/core";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import { PrintOutlined } from "@material-ui/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import validateDateAndFormat from "utils/validateDateAndFormat";
import fileDownload from "js-file-download";
import {
  getBacSlotAvailability,
  printBacSlotAvailability,
  readApiError,
  SLOT_STATUS_OPTIONS,
} from "./BacReportApi";
import { formatFee } from "Clients/ACCB/utils";
import DatePickerACCB from "../../Components/DatePickerACCB";
import FormError from "../../Components/FormError";
import useBACPermissions from "../../hooks/usePermissions";
import { ADMIN_TABS, navigateToAdminTab } from "../adminTabNavigation";

// The report is computed live, so the screen refetches on this interval while
// visible, and when the tab regains focus (at most once per REFOCUS_GAP_MS:
// focus and visibilitychange both fire on a tab switch).
const REFRESH_INTERVAL_MS = 60 * 1000;
const REFOCUS_GAP_MS = 5 * 1000;

// Chip colours per slot status; green/grey match the Book Hearing Room chips.
const STATUS_STYLE = {
  available: { backgroundColor: "#E8F5E9", color: "#2E7D32" },
  booked: { backgroundColor: "#FDECEA", color: "#C62828" },
  past: { backgroundColor: "#F5F5F5", color: "#757575" },
  holiday: { backgroundColor: "#EEEEEE", color: "#616161" },
};

const SUMMARY_TILES = [
  { key: "total", label: "Total" },
  { key: "available", label: "Available" },
  { key: "booked", label: "Booked" },
  { key: "past", label: "Past" },
  { key: "holiday", label: "Holiday" },
];

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    padding: '1rem',
    borderBottom: '1px solid black',
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterField: {
    flex: '1 1 180px',
    maxWidth: 260,
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'none',
    },
  },
  buttonPrint: {
    borderRadius: '0.5rem',
    border: '1px solid #AAAFB9',
    background: '#fff',
    padding: '0.5rem 1rem',
    marginLeft: 'auto',
  },
  dayRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: '0.25rem 1rem',
    padding: '1rem 1rem 0.5rem',
  },
  holidayBanner: {
    margin: '0 1rem 0.75rem',
    padding: '0.6rem 1rem',
    borderRadius: 6,
    border: '1px solid #F5C77E',
    background: '#FFF8E6',
    color: TEXT.warning,
    // A bare div, not Typography, so it doesn't pick up the theme font itself.
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  summaryRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    padding: '0 1rem 1rem',
  },
  summaryTile: {
    flex: '1 1 110px',
    padding: '0.6rem 1rem',
    border: '1px solid #E5E5E5',
    borderRadius: 6,
    background: '#FAFAFA',
  },
  roomBlock: {
    margin: '0 1rem 1rem',
    border: '1px solid #E5E5E5',
    borderRadius: 6,
    overflow: 'hidden',
  },
  roomHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.25rem 1rem',
    padding: '0.6rem 1rem',
    background: '#F5F5F5',
  },
  // Analytics.css styles every bare <table> (80% width, top margin, borders),
  // so pin the table's own layout here.
  slotTable: {
    overflowX: 'auto',
    '& table': {
      width: '100%',
      maxWidth: '100%',
      minWidth: 900,
      height: 'auto',
      margin: 0,
      tableLayout: 'fixed',
    },
    '& th, & td': {
      border: 'none',
      borderBottom: '1px solid #F0F0F0',
      padding: '0.6rem 1rem',
      fontSize: '0.875rem',
      color: TEXT.strong,
      verticalAlign: 'top',
      overflowWrap: 'break-word',
    },
    '& th': {
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    '& tbody tr:last-child td': {
      borderBottom: 'none',
    },
  },
  bookingEntry: {
    '& + &': {
      marginTop: '0.5rem',
    },
  },
}));

const countsText = (summary) => {
  if (!summary) return "";
  const parts = [`${summary.total} slot${summary.total === 1 ? "" : "s"}`];
  ["available", "booked", "past", "holiday"].forEach((key) => {
    if (summary[key]) parts.push(`${summary[key]} ${key}`);
  });
  return parts.join(" · ");
};

export default function BacSlotAvailabilityReport() {
  const classes = useStyles();
  const history = useHistory();
  const { can } = useBACPermissions();

  const [date, setDate] = useState(() => new Date());
  const [roomIds, setRoomIds] = useState([]);
  const [status, setStatus] = useState("All");
  const [roomOptions, setRoomOptions] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  // Only the latest request may update the screen; lastFetch spaces out the
  // focus refetches, and inFlight keeps them from cutting in on a load.
  const requestRef = useRef(0);
  const lastFetchRef = useRef(0);
  const inFlightRef = useRef(false);

  const dateParam = validateDateAndFormat(date, "yyyy-MM-dd");
  const roomParam = roomIds.join(",");

  const load = useCallback((silent = false) => {
    if (!dateParam) {
      setReport(null);
      setError("Select a date to see slot availability.");
      return;
    }
    const requestId = ++requestRef.current;
    lastFetchRef.current = Date.now();
    inFlightRef.current = true;
    if (!silent) {
      setLoading(true);
      setError("");
    }
    getBacSlotAvailability(dateParam, roomParam, status)
      .then((res) => {
        if (requestId !== requestRef.current) return;
        inFlightRef.current = false;
        const data = res?.data?.data || null;
        setReport(data);
        // There is no rooms API: an unfiltered report lists every active
        // room, so the Hearing Room options come from it.
        if (!roomParam && status === "All" && data) {
          setRoomOptions((data.rooms || []).map(({ room_id, room_name }) => ({ room_id, room_name })));
        }
        setUpdatedAt(new Date());
        setError("");
        setLoading(false);
      })
      .catch(async (err) => {
        if (requestId !== requestRef.current) return;
        inFlightRef.current = false;
        console.log('Error: ', err);
        // A failed background refresh keeps what is on screen.
        if (!silent) {
          setReport(null);
          setError((await readApiError(err)) || "Failed to load the report. Please try again.");
        }
        setLoading(false);
      });
  }, [dateParam, roomParam, status]);

  useEffect(() => {
    load(false);
  }, [load]);

  // Keep the screen current: refetch every minute while visible, and when the
  // user comes back to the tab (a booking may have been made meanwhile).
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    const refreshIfVisible = (minGap) => {
      if (document.hidden || inFlightRef.current) return;
      if (Date.now() - lastFetchRef.current < minGap) return;
      loadRef.current(true);
    };
    const onFocus = () => refreshIfVisible(REFOCUS_GAP_MS);
    const timer = setInterval(() => refreshIfVisible(0), REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const handlePrintReport = () => {
    if (!dateParam) {
      setError("Select a date to see slot availability.");
      return;
    }
    setPrinting(true);
    printBacSlotAvailability(dateParam, roomParam, status)
      .then((res) => {
        fileDownload(res.data, `slot_availability_${dateParam}.pdf`);
        setPrinting(false);
      })
      .catch(async (err) => {
        console.log(err);
        setError((await readApiError(err)) || "Failed to download the report. Please try again.");
        setPrinting(false);
      });
  };

  // Open the case in View Case, as Overview does when a case is clicked.
  const openCase = (caseId) => {
    sessionStorage.setItem("bac_overview_caseid", caseId);
    navigateToAdminTab(history, ADMIN_TABS.OVERVIEW);
  };

  const renderBooking = (booking) => {
    const details = [
      booking.case_title,
      booking.reserved_for && `Reserved for ${booking.reserved_for}`,
    ].filter(Boolean).join(" · ");
    return (
      <Box key={booking.causelist_id} className={classes.bookingEntry}>
        {booking.dispute_id && can("case_overview") ? (
          <Link component="button" onClick={() => openCase(booking.dispute_id)} style={{ textAlign: 'left', fontWeight: 600, fontSize: '0.875rem' }}>
            {booking.case_number || `Case ${booking.dispute_id}`}
          </Link>
        ) : (
          <Typography style={{ fontWeight: 600, fontSize: '0.875rem' }}>{booking.case_number || '—'}</Typography>
        )}
        {details && (
          <Typography style={{ fontSize: '0.75rem', color: TEXT.muted }}>{details}</Typography>
        )}
      </Box>
    );
  };

  // One line per booking, so a double booking's cause list / stage line up
  // with its case in the Case column.
  const renderBookingField = (bookings, field) =>
    bookings.length
      ? bookings.map((booking) => (
          <Box key={booking.causelist_id} className={classes.bookingEntry}>
            {booking[field] || '—'}
          </Box>
        ))
      : '—';

  const renderRoom = (room) => {
    const fullDayFee = formatFee(room.full_day_fee);
    return (
      <Box key={room.room_id} className={classes.roomBlock}>
        <Box className={classes.roomHeader}>
          <Box>
            <Typography style={{ fontWeight: 600, fontSize: '0.95rem', color: TEXT.strong }}>
              {room.room_name || `Room ${room.room_id}`}
            </Typography>
            <Typography style={{ fontSize: '0.75rem', color: TEXT.muted }}>
              {countsText(room.summary)}
            </Typography>
          </Box>
          <Box display="flex" style={{ gap: '1rem', flexWrap: 'wrap' }}>
            {room.seating_capacity && (
              <Typography style={{ fontSize: '0.8rem', color: TEXT.secondary, whiteSpace: 'nowrap' }}>
                Seating Capacity: <strong style={{ color: TEXT.primary }}>{room.seating_capacity}</strong>
              </Typography>
            )}
            {fullDayFee && (
              <Typography style={{ fontSize: '0.8rem', color: TEXT.secondary, whiteSpace: 'nowrap' }}>
                Full day: <strong style={{ color: TEXT.primary }}>{fullDayFee}</strong>
              </Typography>
            )}
          </Box>
        </Box>
        <Box className={classes.slotTable}>
          <Table size="small">
            <colgroup>
              <col style={{ width: 180 }} />
              <col style={{ width: 130 }} />
              <col />
              <col style={{ width: 160 }} />
              <col style={{ width: 220 }} />
              <col style={{ width: 100 }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Case</TableCell>
                <TableCell>Cause List</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Fee</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(room.slots || []).map((slot) => {
                const bookings = slot.bookings || [];
                return (
                  <TableRow key={slot.slot_id}>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{slot.time_label}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={slot.status_label || slot.status}
                        style={{ fontWeight: 600, ...(STATUS_STYLE[slot.status] || {}) }}
                      />
                      {bookings.length > 1 && (
                        <Typography style={{ fontSize: '0.75rem', color: TEXT.error, fontWeight: 600, marginTop: '0.25rem' }}>
                          Double booked
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{bookings.length ? bookings.map(renderBooking) : '—'}</TableCell>
                    <TableCell>{renderBookingField(bookings, "listing_type_label")}</TableCell>
                    <TableCell>{renderBookingField(bookings, "stage")}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{formatFee(slot.applicable_fee) || '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>
    );
  };

  const roomName = (id) => {
    const room = roomOptions.find((r) => r.room_id === id);
    return room ? room.room_name : `Room ${id}`;
  };

  return (
    <Box>
      <Typography style={{ fontSize: '1.5rem', padding: '2rem' }}>
        SLOT AVAILABILITY REPORT
      </Typography>
      <Box style={{ backgroundColor: 'white' }}>
        <Box className={classes.filtersContainer}>
          <Box className={classes.filterField}>
            <DatePickerACCB label="Date" value={date} onChange={setDate} />
          </Box>
          <FormControl fullWidth size="small" margin="none" variant="outlined" className={classes.filterField}>
            <InputLabel shrink>Hearing Room</InputLabel>
            <Select
              multiple
              displayEmpty
              notched
              label="Hearing Room"
              value={roomIds}
              onChange={(e) => setRoomIds(e.target.value)}
              renderValue={(selected) => (selected.length ? selected.map(roomName).join(", ") : "All rooms")}
            >
              {roomOptions.map((room) => (
                <MenuItem key={room.room_id} value={room.room_id} dense>
                  <Checkbox size="small" color="primary" checked={roomIds.includes(room.room_id)} />
                  <ListItemText primary={room.room_name || `Room ${room.room_id}`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" margin="none" variant="outlined" className={classes.filterField}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {SLOT_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            className={classes.buttonPrint}
            size="small"
            endIcon={printing ? <CircularProgress size={16} color="inherit" /> : <PrintOutlined />}
            onClick={handlePrintReport}
            disabled={printing}
          >
            {printing ? "Downloading..." : "Download PDF"}
          </Button>
        </Box>

        {error && (
          <Box style={{ padding: '0.5rem 1rem 0' }}>
            <FormError message={error} />
          </Box>
        )}

        {loading ? (
          <Box style={{ margin: 'auto', width: '100%', padding: '2rem 0' }}>
            <CircularCenteredLoader />
          </Box>
        ) : report && (
          <Box style={{ paddingBottom: '0.5rem' }}>
            <Box className={classes.dayRow}>
              <Typography style={{ fontWeight: 600, color: TEXT.strong }}>
                {[report.day_name, report.date_label].filter(Boolean).join(", ")}
              </Typography>
              {updatedAt && (
                <Typography style={{ fontSize: '0.75rem', color: TEXT.muted }}>
                  Updated {updatedAt.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}
                </Typography>
              )}
            </Box>

            {report.is_holiday && (
              <Box className={classes.holidayBanner}>
                Non-working / holiday date: hearing rooms can't be booked on this day.
              </Box>
            )}

            {report.summary && (
              <Box className={classes.summaryRow}>
                {SUMMARY_TILES.map((tile) => (
                  <Box key={tile.key} className={classes.summaryTile}>
                    <Typography style={{ fontSize: '0.75rem', color: TEXT.muted, textTransform: 'uppercase', fontWeight: 500 }}>
                      {tile.label}
                    </Typography>
                    <Typography style={{ fontSize: '1.25rem', fontWeight: 700, color: STATUS_STYLE[tile.key]?.color || TEXT.strong }}>
                      {report.summary[tile.key] ?? 0}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {(report.rooms || []).length ? (
              report.rooms.map(renderRoom)
            ) : (
              <Typography style={{ padding: '2rem 8px', textAlign: 'center', fontSize: '1.5rem' }}>
                No slots match these filters.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
